/**
 * @description Map 객체의 확장 메서드를 정의합니다.
 */

import { isFunction } from './utils';

declare global {
  interface Map<K, V> {
    /**
     * 키로 값을 조회하고, 없으면 기본값을 저장한 후 반환합니다.
     * Python의 dict.setdefault()와 유사한 동작입니다.
     * @param key - 조회할 키
     * @param setDefault - 기본값 또는 기본값을 반환하는 함수
     * @returns 조회된 값 또는 새로 저장된 기본값
     * @example
     * ```ts
     * const map = new Map<string, number[]>();
     * const arr = map.getEx('key', () => []); // 새 배열 생성 후 반환
     * arr.push(1); // map.get('key')는 이제 [1]
     * ```
     */
    getEx(key: K, setDefault: V | (() => V)): V;

    /**
     * 지정된 키들만 포함하는 새로운 Map을 반환합니다.
     * lodash의 pick 함수와 유사한 동작입니다.
     * @param keys - 포함할 키 배열
     * @returns 필터링된 새 Map
     * @example
     * ```ts
     * const map = new Map([['a', 1], ['b', 2], ['c', 3]]);
     * const picked = map.pick(['a', 'c']); // Map { 'a' => 1, 'c' => 3 }
     * ```
     */
    pick(keys: K[]): Map<K, V>;
  }
}

Map.prototype.getEx = function <K, V>(key: K, setDefault: V | (() => V)): V {
  let v = this.get(key);
  if (!v && setDefault) {
    v = isFunction(setDefault) ? setDefault() : setDefault;
    this.set(key, v);
    return v;
  }
  return v;
};

Map.prototype.pick = function <K, V>(keys: K[]): Map<K, V> {
  const result = new Map<K, V>();
  for (const key of keys) {
    if (this.has(key)) {
      result.set(key, this.get(key));
    }
  }
  return result;
};

export {};
